import React, { useEffect, useRef, useState } from 'react'
import { ActivityIndicator, StyleSheet, View, Dimensions, Keyboard, Text, TouchableOpacity } from 'react-native'
import { useSelector, useDispatch } from 'react-redux'

import styles, { colors } from '../../../styles/global'
import CustomScrollView from '../../../UIControls/CustomScrollView'
import EditForm from './EditForm'
import { applyPopoverWidthV2 } from '../../../../utils/HelperFunctions'
import useWindowSize from '../../../../utils/useWindowSize'
import CloseButton from './CloseButton'
import CommentsList from './CommentsList'
import AttachmentsSelectorModal from '../AttachmentsSelectorModal'
import {
    insertAttachmentInsideEditor,
    RECORD_SCREEN_MODAL_ID,
    RECORD_VIDEO_MODAL_ID,
} from '../../../Feeds/CommentsTextInput/textInputHelper'
import { updateNewAttachmentsData } from '../../../Feeds/Utils/HelperFunctions'
import { exportRef } from '../../../NotesView/NotesDV/EditorView/NotesEditorView'
import {
    BOT_OPTION_MODAL_ID,
    BOT_WARNING_MODAL_ID,
    COMMENT_MODAL_ID,
    MENTION_MODAL_ID,
    removeModal,
    RUN_OUT_OF_GOLD_MODAL_ID,
    storeModal,
} from '../../../ModalsManager/modalsManager'
import useGetMessages from '../../../../hooks/Chats/useGetMessages'
import { sortBy } from 'lodash'
import ShowMoreButton from '../../../UIControls/ShowMoreButton'
import { DV_TAB_NOTE_EDITOR } from '../../../../utils/TabNavigationConstants'
import URLTrigger from '../../../../URLSystem/URLTrigger'
import NavigationService from '../../../../utils/NavigationService'
import { checkIsLimitedByTraffic } from '../../../Premium/PremiumHelper'
import { setActiveChatData, setAssistantEnabled } from '../../../../redux/actions'
import BotMessagePlaceholder from './BotMessagePlaceholder'
import { CHAT_INPUT_LIMIT_IN_CHARACTERS } from '../../../../utils/assistantHelper'
import { getAssistant } from '../../../AdminPanel/Assistants/assistantsHelper'
import { getDvChatTabLink } from '../../../../utils/LinkingHelper'
import {
    getChatCommentsWithLinkedEmails,
    markChatMessagesAsRead,
    getParentObjectData,
} from '../../../../utils/backends/Chats/chatsComments'
import { translate } from '../../../../i18n/TranslationService'
import {
    hasAssistantReplyToPendingSend,
    hasLoadingAssistantMessage,
    hasNewVisibleAssistantMessage,
    shouldShowAssistantScrollIndicator,
    snapshotAssistantMessageIds,
} from '../../../ChatsView/Utils/assistantWaiting'
import {
    archiveAndMarkReadLinkedEmails,
    getLinkedEmailsFromMessages,
} from '../../../ChatsView/ChatDV/linkedEmailActions'
import Icon from '../../../Icon'
import SharedHelper from '../../../../utils/SharedHelper'
import AttachmentDropZone from '../../../Feeds/CommentsTextInput/AttachmentDropZone'
import useNewEmailCommentIds from '../../../ChatsView/ChatDV/useNewEmailCommentIds'
import CommentPopupObjectHeader from './CommentPopupObjectHeader'
import useCommentPopupAutoFocus, {
    COMMENT_POPUP_FOCUS_BLUR,
    COMMENT_POPUP_FOCUS_DELAYED,
    resolveCommentPopupFocusAction,
} from './commentPopupAutoFocus'
import RichCommentDismissSurface from './RichCommentDismissSurface'
import DeferredCommentComposer from './DeferredCommentComposer'
import PendingCommentSends from './PendingCommentSends'
import { readCommentDraft, saveCommentDraft, clearCommentDraft } from '../../../../utils/commentDraftStore'
import { getTimestampInMilliseconds } from '../../../ChatsView/Utils/ChatHelper'
import {
    ASSISTANT_LOADING_TIMEOUT_MS,
    resolveEffectiveMessageLoading,
} from '../../../ChatsView/ChatDV/EditorView/messageLoadingState'
import { useAssistantLinePendingSendForChat } from '../../../MyDayView/AssistantLine/assistantLinePendingSend'
import { subscribeBotSpinnerWaitEnd } from '../../../ChatsView/Utils/botSpinnerTrigger'
import { getSafeAreaModalMaxHeight } from '../../../../utils/modalSafeArea'

export default function RichCommentModal({
    projectId,
    objectType,
    objectId,
    closeModal,
    processDone,
    currentComment,
    currentMentions,
    currentPrivacy,
    currentKarma,
    commentsLengthRef,
    inNotesEditor,
    inTaskModal,
    inSuggested,
    userGettingKarmaId,
    customHeader,
    showBotButton,
    objectName,
    initialObject,
    externalAssistantId,
    initialAssistantEnabled = false,
    openedFromUnreadComment = false,
}) {
    const dispatch = useDispatch()
    const isMiddleScreen = useSelector(state => state.isMiddleScreen)
    const smallScreenNavigation = useSelector(state => state.smallScreenNavigation)
    const loggedUser = useSelector(state => state.loggedUser)
    const gold = useSelector(state => state.loggedUser.gold)
    const chatNotifications = useSelector(state => state.projectChatNotifications[projectId][objectId])
    const botOptionModalIsOpen = useSelector(state => state.openModals[BOT_OPTION_MODAL_ID])
    const botWarningModalIsOpen = useSelector(state => state.openModals[BOT_WARNING_MODAL_ID])
    const mentionModalIsOpen = useSelector(state => state.openModals[MENTION_MODAL_ID])
    const recordScreenModalIsOpen = useSelector(state => state.openModals[RECORD_SCREEN_MODAL_ID])
    const recordVideoModalIsOpen = useSelector(state => state.openModals[RECORD_VIDEO_MODAL_ID])
    const runOutOfGoldModalIsOpen = useSelector(state => state.openModals[RUN_OUT_OF_GOLD_MODAL_ID])
    const isQuillTagEditorOpen = useSelector(state => state.isQuillTagEditorOpen)
    const assistantEnabled = useSelector(state => state.assistantEnabled)
    const showNotificationAboutTheBotBehavior = useSelector(state => state.showNotificationAboutTheBotBehavior)
    const [width, height] = useWindowSize()
    const editForm = useRef()
    const [assistantId, setAssistantId] = useState(externalAssistantId)
    const [showFileSelector, setShowFileSelector] = useState(false)
    const [inputCursorIndex, setInputCursorIndex] = useState(0)
    const [editor, setEditor] = useState(null)
    const [waitingForBotAnswer, setWaitingForBotAnswer] = useState(false)
    const [showRunOutGoalModal, setShowRunOutGoalModal] = useState(false)
    const [windowWidth, setWindowWidth] = useState(Dimensions.get('window').width)
    const selectedTab = useSelector(state => state.selectedNavItem)
    const editorOpsRef = useRef([])
    const commentListRef = useRef()
    const assistantMessageIdsAtWaitStartRef = useRef(new Set())
    const modalMountedRef = useRef(true)
    const [isThreadAssistantEnabled, setIsThreadAssistantEnabled] = useState(initialAssistantEnabled)
    const draftContext = { userId: loggedUser.uid, projectId, objectType, objectId }
    const [initialComment, setInitialComment] = useState(
        () => readCommentDraft(draftContext)?.comment || currentComment || ''
    )
    const [sendError, setSendError] = useState('')
    const [isSending, setIsSending] = useState(false)
    const sendingRef = useRef(false)
    const persistDraft = comment => {
        setInitialComment(comment)
        if (!saveCommentDraft(draftContext, { ...readCommentDraft(draftContext), comment })) {
            setSendError(translate('Could not save on this device. Keep this window open and try again.'))
        }
    }
    const [archivingEmailKeys, setArchivingEmailKeys] = useState([])
    const [archivedEmailKeys, setArchivedEmailKeys] = useState([])
    const [archivingAllEmails, setArchivingAllEmails] = useState(false)
    const messages = useGetMessages(
        true,
        true,
        projectId,
        objectId,
        objectType === 'users' ? 'contacts' : objectType,
        3
    )
    const pendingLineSend = useAssistantLinePendingSendForChat(projectId, objectId)

    const disableDoneButton = !!botOptionModalIsOpen || isSending

    const totalFollowed = chatNotifications ? chatNotifications.totalFollowed : 0
    const totalUnfollowed = chatNotifications ? chatNotifications.totalUnfollowed : 0
    const chatNotificationsAmount = totalFollowed || totalUnfollowed
    const shouldAutoFocusInput = useCommentPopupAutoFocus({
        mobile: smallScreenNavigation,
        chatNotifications,
        openedFromUnreadComment,
    })

    const comments = sortBy(messages, [item => -item.created])
    const newEmailCommentIds = useNewEmailCommentIds(`${projectId}:${objectType}:${objectId}`, chatNotifications)
    const linkedEmails = getLinkedEmailsFromMessages(comments, { projectId, chatId: objectId })
    const unarchivedLinkedEmails = linkedEmails.filter(email => !archivedEmailKeys.includes(email.key))
    const canArchiveLinkedEmails = SharedHelper.accessGranted(loggedUser, projectId)
    const hasNewAssistantMessage = hasNewVisibleAssistantMessage(
        comments,
        assistantMessageIdsAtWaitStartRef.current,
        getAssistant
    )
    const hasActiveAssistantMessage = hasLoadingAssistantMessage(
        comments,
        creatorId => creatorId === assistantId || !!getAssistant(creatorId),
        message =>
            message.assistantRun?.kind === 'chat' &&
            resolveEffectiveMessageLoading(message, getTimestampInMilliseconds(message.lastChangeDate))
    )
    const lineReplyIsVisible = hasAssistantReplyToPendingSend(
        comments,
        pendingLineSend,
        creatorId => !!getAssistant(creatorId)
    )
    const showBotPlaceholder =
        !hasActiveAssistantMessage &&
        ((waitingForBotAnswer && !hasNewAssistantMessage) || (!!pendingLineSend && !lineReplyIsVisible))
    const assistantResponseIsLoading = showBotPlaceholder || hasActiveAssistantMessage

    const toggleShowFileSelector = () => {
        if (showFileSelector || !checkIsLimitedByTraffic(projectId)) {
            const newValue = !showFileSelector
            setShowFileSelector(newValue)
            if (newValue) {
                editorOpsRef.current = []
            }
        } else {
            closeModal()
        }
    }

    const archiveLinkedEmails = emails => {
        const pendingEmails = emails.filter(
            email => !archivedEmailKeys.includes(email.key) && !archivingEmailKeys.includes(email.key)
        )
        if (pendingEmails.length === 0) return

        const pendingKeys = pendingEmails.map(email => email.key)
        setArchivingEmailKeys(current => [...new Set([...current, ...pendingKeys])])
        // Optimistic, like the chat list and the thread (AT-2424): the press has already cleared
        // the comment's unread state, so the button says "Archived" from the same frame and only
        // comes back if the mailbox refuses.
        setArchivedEmailKeys(current => [...new Set([...current, ...pendingKeys])])
        archiveAndMarkReadLinkedEmails(pendingEmails)
            .catch(error => {
                // The action put the unread state back; the button has to come back with it.
                if (modalMountedRef.current) {
                    setArchivedEmailKeys(current => current.filter(key => !pendingKeys.includes(key)))
                }
                console.error('Failed to archive linked email from comment popup', error)
                alert(`${translate("Email couldn't be archived")}: ${error.message}`)
            })
            .finally(() => {
                if (modalMountedRef.current) {
                    setArchivingEmailKeys(current => current.filter(key => !pendingKeys.includes(key)))
                }
            })
    }

    const archiveAllLinkedEmails = () => {
        if (archivingAllEmails) return
        setArchivingAllEmails(true)
        closeModal()

        const chatType = objectType === 'users' ? 'contacts' : objectType
        getChatCommentsWithLinkedEmails(projectId, chatType, objectId)
            .then(allLinkedEmailComments =>
                archiveAndMarkReadLinkedEmails(
                    getLinkedEmailsFromMessages(allLinkedEmailComments, { projectId, chatId: objectId })
                )
            )
            .catch(error => {
                console.error('Failed to archive linked emails from comment popup', error)
                alert(`${translate("Emails couldn't be archived")}: ${error.message}`)
            })
    }

    useEffect(() => {
        modalMountedRef.current = true
        return () => {
            modalMountedRef.current = false
        }
    }, [])

    useEffect(() => {
        // Diagnostics only — and the Firestore read below exists solely to fill in the log, so the
        // whole effect stands down in production rather than just the console call.
        if (__DEV__ && messages.loaded && comments.length === 0) {
            getParentObjectData(projectId, objectId, objectType)
                .then(data => {
                    const commentsData = data?.object?.commentsData || null
                    console.warn('[TaskComments] Comment popup loaded with no visible comments', {
                        projectId,
                        objectId,
                        objectType,
                        hasChatNotifications: chatNotificationsAmount > 0,
                        hasPreviewMetadata: !!commentsData,
                        previewCommentsAmount: commentsData?.amount || 0,
                    })
                })
                .catch(error => {
                    console.warn('[TaskComments] Could not load comment parent object', {
                        projectId,
                        objectId,
                        objectType,
                        error,
                    })
                })
        }
    }, [messages.loaded, comments.length, projectId, objectId, objectType, chatNotificationsAmount])

    useEffect(() => {
        setAssistantId(externalAssistantId)
    }, [externalAssistantId])

    useEffect(() => {
        getParentObjectData(projectId, objectId, objectType)
            .then(data => {
                if (data && data.object) {
                    const threadEnabled = data.object.isAssistantEnabled === true
                    setIsThreadAssistantEnabled(threadEnabled)
                    dispatch(setAssistantEnabled(threadEnabled))
                } else {
                    setIsThreadAssistantEnabled(false)
                    dispatch(setAssistantEnabled(false))
                }
            })
            .catch(error => {
                console.warn('[TaskComments] Could not load assistant state for parent object', {
                    projectId,
                    objectId,
                    objectType,
                    error,
                })
                setIsThreadAssistantEnabled(false)
                dispatch(setAssistantEnabled(false))
            })
        return () => {
            dispatch(setAssistantEnabled(false))
        }
    }, [projectId, objectId, objectType])

    useEffect(() => {
        const subscription = Dimensions.addEventListener('change', ({ window, screen }) => {
            setWindowWidth(window.width)
        })
        return () => subscription?.remove()
    })

    const updateObjectState = updatedObj => {
        if (updatedObj && updatedObj.isAssistantEnabled !== undefined) {
            setIsThreadAssistantEnabled(updatedObj.isAssistantEnabled === true)
        }
    }

    const done = async ({ comment, mentions, privacy, hasKarma }) => {
        if (sendingRef.current) return
        const shouldTriggerAssistant = isThreadAssistantEnabled === true
        if (shouldTriggerAssistant && gold === 0) {
            setShowRunOutGoalModal(true)
            dispatch(setAssistantEnabled(false))
            return
        }
        sendingRef.current = true
        setIsSending(true)
        setSendError('')
        try {
            if (!saveCommentDraft(draftContext, { comment, mentions, privacy, hasKarma })) {
                throw new Error(translate('Could not save on this device. Keep this window open and try again.'))
            }
            if (shouldTriggerAssistant) {
                assistantMessageIdsAtWaitStartRef.current = snapshotAssistantMessageIds(comments, getAssistant)
                setWaitingForBotAnswer(true)
            }
            const text = inTaskModal ? comment : await updateNewAttachmentsData(projectId, comment)
            await processDone(text.trim(), mentions, privacy, hasKarma, shouldTriggerAssistant)
            clearCommentDraft(draftContext, comment)
            if (modalMountedRef.current) {
                editor?.setText('')
                editor?.setSelection(0)
                setInitialComment('')
                if (!inNotesEditor && !shouldTriggerAssistant) closeModal()
            }
        } catch (error) {
            if (modalMountedRef.current) {
                setWaitingForBotAnswer(false)
                setSendError(error.message || translate('Comment could not be sent. Your text is still here.'))
            }
            console.error('[RichCommentModal] Could not send comment', error)
        } finally {
            sendingRef.current = false
            if (modalMountedRef.current) setIsSending(false)
        }
    }

    const addAttachmentTag = (text, uri) => {
        insertAttachmentInsideEditor(inputCursorIndex, editor, text, uri)
        const ops = editor.getContents().ops
        editorOpsRef.current = ops
        setInputCursorIndex(inputCursorIndex + 3)
    }

    useEffect(() => {
        if (waitingForBotAnswer && hasNewAssistantMessage) setWaitingForBotAnswer(false)
    }, [waitingForBotAnswer, hasNewAssistantMessage])

    useEffect(() => {
        if (!isThreadAssistantEnabled) {
            setWaitingForBotAnswer(false)
        }
    }, [isThreadAssistantEnabled])

    useEffect(
        () =>
            subscribeBotSpinnerWaitEnd((endedProjectId, endedObjectId) => {
                if (endedProjectId === projectId && endedObjectId === objectId) setWaitingForBotAnswer(false)
            }),
        [projectId, objectId]
    )

    useEffect(() => {
        if (!waitingForBotAnswer) return undefined
        const timeout = setTimeout(() => setWaitingForBotAnswer(false), ASSISTANT_LOADING_TIMEOUT_MS)
        return () => clearTimeout(timeout)
    }, [waitingForBotAnswer])

    useEffect(() => {
        if (commentsLengthRef) {
            commentsLengthRef.current = messages.length
        }
    }, [messages])

    useEffect(() => {
        // On mobile the input is focused by CustomTextInput3's own mount-time
        // `autoFocus` (passed to EditForm below), which runs inside the tap that
        // opened the pop-up — being inside that gesture is precisely what lets the
        // browser raise the software keyboard (AT-2269). This is the same path
        // `TaskInput` has always used to open the keyboard when a task line turns
        // into an editor, so nothing new is being relied on.
        //
        // The delayed re-focus below is therefore skipped on mobile. It could not
        // open the keyboard even if it wanted to — a second later it is long
        // outside the gesture — while it can very easily re-open one the user just
        // dismissed to read the comments underneath. It also focuses the notes
        // document editor via `exportRef`, whose scroll-into-view is deliberately
        // unconfined (AT-2220), which on a phone would drag the page around.
        const focusAction = resolveCommentPopupFocusAction({
            inSuggested,
            shouldAutoFocus: shouldAutoFocusInput,
            mobile: smallScreenNavigation,
        })

        if (focusAction === COMMENT_POPUP_FOCUS_BLUR) {
            exportRef?.getEditor()?.blur()
            editForm?.current?.blur()
            Keyboard.dismiss()
            return
        }

        if (focusAction !== COMMENT_POPUP_FOCUS_DELAYED) return

        const focusTimeout = setTimeout(() => {
            exportRef?.getEditor()?.focus()
            editForm?.current?.focus()
        }, 1000)
        return () => clearTimeout(focusTimeout)
    }, [inSuggested, shouldAutoFocusInput, smallScreenNavigation])

    useEffect(() => {
        storeModal(COMMENT_MODAL_ID)

        return () => {
            removeModal(COMMENT_MODAL_ID)
        }
    }, [])

    useEffect(() => {
        if (!showFileSelector) {
            dispatch(setActiveChatData(projectId, objectId, objectType))
            return () => {
                dispatch(setActiveChatData('', '', ''))
            }
        }
    }, [showFileSelector])

    useEffect(() => {
        if (chatNotificationsAmount > 0) {
            markChatMessagesAsRead(projectId, objectId)
        }
    }, [chatNotificationsAmount])

    const processShowMore = () => {
        if (selectedTab === DV_TAB_NOTE_EDITOR) {
            closeModal(true)
            navigateToComments()
        } else {
            navigateToComments()
        }
    }

    const navigateToComments = () => {
        const path = getDvChatTabLink(projectId, objectId, objectType === 'topics' ? 'chats' : objectType)

        setTimeout(() => closeModal(), 50)
        URLTrigger.processUrl(NavigationService, path)
    }

    const nestedPopupIsOpen =
        botOptionModalIsOpen ||
        botWarningModalIsOpen ||
        mentionModalIsOpen ||
        recordScreenModalIsOpen ||
        recordVideoModalIsOpen ||
        runOutOfGoldModalIsOpen ||
        isQuillTagEditorOpen ||
        showRunOutGoalModal

    return showNotificationAboutTheBotBehavior ? null : (
        <RichCommentDismissSurface disabled={!!nestedPopupIsOpen} onDismiss={closeModal}>
            {showFileSelector ? (
                <AttachmentsSelectorModal
                    closeModal={toggleShowFileSelector}
                    addAttachmentTag={addAttachmentTag}
                    projectId={projectId}
                />
            ) : (
                <CustomScrollView
                    style={[
                        localStyles.container,
                        applyPopoverWidthV2(isMiddleScreen, smallScreenNavigation, windowWidth),
                        { maxHeight: getSafeAreaModalMaxHeight(height, 64) },
                    ]}
                    showsVerticalScrollIndicator={false}
                    showIndicator={shouldShowAssistantScrollIndicator(
                        smallScreenNavigation,
                        assistantResponseIsLoading
                    )}
                    fixedChildren={
                        <CloseButton closeModal={closeModal} comments={comments} openChat={processShowMore} />
                    }
                >
                    <View style={localStyles.innerContainer}>
                        {customHeader}
                        <CommentPopupObjectHeader
                            projectId={projectId}
                            objectType={objectType}
                            objectId={objectId}
                            objectName={objectName}
                            initialObject={initialObject}
                            onOpen={processShowMore}
                            onWorkflowTransitionSuccess={closeModal}
                        />

                        {canArchiveLinkedEmails && linkedEmails.length > 0 && (
                            <View style={localStyles.emailActionsBar}>
                                <TouchableOpacity
                                    style={localStyles.emailActionButton}
                                    onPress={archiveAllLinkedEmails}
                                    disabled={
                                        unarchivedLinkedEmails.length === 0 ||
                                        archivingEmailKeys.length > 0 ||
                                        archivingAllEmails
                                    }
                                    accessibilityLabel={translate('Archive all emails')}
                                >
                                    {archivingEmailKeys.length > 0 || archivingAllEmails ? (
                                        <ActivityIndicator size="small" color={colors.UtilityBlue125} />
                                    ) : (
                                        <Icon
                                            name={unarchivedLinkedEmails.length === 0 ? 'check' : 'archive'}
                                            size={14}
                                            color={colors.UtilityBlue125}
                                        />
                                    )}
                                    <Text style={localStyles.emailActionText}>
                                        {translate(
                                            unarchivedLinkedEmails.length === 0 ? 'Archived' : 'Archive all emails'
                                        )}
                                    </Text>
                                </TouchableOpacity>
                            </View>
                        )}

                        <PendingCommentSends projectId={projectId} objectId={objectId} objectType={objectType} dark />
                        {!!sendError && (
                            <Text accessibilityRole="alert" style={{ color: '#ffffff', marginBottom: 8 }}>
                                {sendError}
                            </Text>
                        )}
                        <DeferredCommentComposer
                            defer={!smallScreenNavigation}
                            resetKey={`${projectId}:${objectType}:${objectId}`}
                        >
                            <AttachmentDropZone
                                testID="modal-chat-image-drop-zone"
                                disabled={loggedUser.isAnonymous}
                                editor={editor}
                                inputCursorIndex={inputCursorIndex}
                                projectId={projectId}
                                setInputCursorIndex={setInputCursorIndex}
                            >
                                <EditForm
                                    ref={editForm}
                                    projectId={projectId}
                                    containerStyle={{ marginBottom: comments && comments.length > 0 ? 16 : 0 }}
                                    onSuccess={done}
                                    currentComment={initialComment}
                                    currentMentions={readCommentDraft(draftContext)?.mentions || currentMentions}
                                    currentPrivacy={readCommentDraft(draftContext)?.privacy ?? currentPrivacy}
                                    currentKarma={readCommentDraft(draftContext)?.hasKarma ?? currentKarma}
                                    toggleShowFileSelector={toggleShowFileSelector}
                                    setEditor={setEditor}
                                    editor={editor}
                                    setInputCursorIndex={setInputCursorIndex}
                                    initialCursorIndex={inputCursorIndex}
                                    initialDeltaOps={editorOpsRef.current.length > 0 ? editorOpsRef.current : null}
                                    setInitialComment={persistDraft}
                                    isSubmitting={isSending}
                                    loggedUserId={loggedUser.uid}
                                    userGettingKarmaId={userGettingKarmaId}
                                    objectType={objectType}
                                    userIsAnonymous={loggedUser.isAnonymous}
                                    showBotButton={showBotButton}
                                    setShowRunOutGoalModal={setShowRunOutGoalModal}
                                    showRunOutGoalModal={showRunOutGoalModal}
                                    objectId={objectId}
                                    characterLimit={CHAT_INPUT_LIMIT_IN_CHARACTERS}
                                    disableDoneButton={disableDoneButton}
                                    assistantId={assistantId}
                                    setAssistantId={setAssistantId}
                                    chatAssistantData={{
                                        objectId,
                                        objectAssistantId: assistantId,
                                        objectType: objectType,
                                    }}
                                    isAssistantEnabled={isThreadAssistantEnabled}
                                    updateObjectState={updateObjectState}
                                    autoFocus={shouldAutoFocusInput}
                                />
                            </AttachmentDropZone>
                        </DeferredCommentComposer>
                        {showBotPlaceholder && (
                            <BotMessagePlaceholder projectId={projectId} assistantId={assistantId} />
                        )}
                        <div ref={commentListRef}>
                            {comments && comments.length > 0 && (
                                <CommentsList
                                    projectId={projectId}
                                    objectType={objectType}
                                    objectId={objectId}
                                    comments={comments}
                                    newEmailCommentIds={newEmailCommentIds}
                                    canArchiveLinkedEmails={canArchiveLinkedEmails}
                                    archivingAllEmails={archivingAllEmails}
                                    archivingEmailKeys={archivingEmailKeys}
                                    archivedEmailKeys={archivedEmailKeys}
                                    onArchiveLinkedEmail={archiveLinkedEmails}
                                />
                            )}
                            {messages.loaded && comments.length === 0 && (
                                <View style={localStyles.emptyState}>
                                    <Text style={[localStyles.emptyStateTitle, { color: '#FFFFFF' }]}>
                                        {translate('No comments yet')}
                                    </Text>
                                    <Text style={[localStyles.emptyStateText, { color: colors.UtilityBlue125 }]}>
                                        {translate('There are no visible comments to show right now.')}
                                    </Text>
                                </View>
                            )}
                        </div>
                        <ShowMoreButton
                            expand={processShowMore}
                            contract={processShowMore}
                            expanded={false}
                            expandText={'open chat'}
                            style={{ marginTop: 8 }}
                        />
                    </View>
                </CustomScrollView>
            )}
        </RichCommentDismissSurface>
    )
}

const localStyles = StyleSheet.create({
    container: {
        flexDirection: 'column',
        paddingHorizontal: 8,
        paddingVertical: 8,
        borderRadius: 4,
        backgroundColor: colors.Secondary400,
        boxShadow: '0px 4px 16px rgba(78,93,120,0.56)',
        elevation: 3,
        height: 'auto',
        maxWidth: 305,
        minWidth: 305,
    },
    innerContainer: {
        paddingHorizontal: 8,
        paddingVertical: 8,
    },
    emptyState: {
        borderRadius: 8,
        backgroundColor: colors.Secondary250,
        paddingHorizontal: 12,
        paddingVertical: 12,
        borderWidth: 1,
        borderColor: colors.Primary350,
    },
    emptyStateTitle: {
        ...styles.subtitle1,
        color: '#FFFFFF',
        marginBottom: 4,
    },
    emptyStateText: {
        ...styles.caption1,
        color: colors.Text04,
    },
    emailActionsBar: {
        alignItems: 'flex-end',
        marginBottom: 8,
    },
    emailActionButton: {
        minHeight: 28,
        paddingHorizontal: 8,
        borderRadius: 4,
        borderWidth: 1,
        borderColor: colors.Primary350,
        flexDirection: 'row',
        alignItems: 'center',
    },
    emailActionText: {
        ...styles.caption2,
        color: colors.UtilityBlue125,
        marginLeft: 6,
    },
})
